import { Button } from '@chakra-ui/button'
import { Flex, Stack, useDisclosure } from '@chakra-ui/react'
import type { WizardVehicleAdditionalData } from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelectedQuote } from '@/context/SelectedQuoteContext'
import { useSidebar } from '@/context/SidebarContext'
import { useApp } from '@/hooks/useApp'
import { useNameof } from '@/hooks/useNameof'
import { useSaveAdditionalData, useUpdateLastWizardStep } from '@/hooks/useWizard'
import { Step3Sections } from '@/pages/steps/vehicle/3'

import { ConfirmModal } from '@/components'
import { Apps } from '@/dtos'
import type { PageSection as Section } from '@/layouts/components/Sidebar'
import { FormSection } from '../../../forms/FormSection'
import { AdditionalContactInfoForm } from '../../vehicle/Step3/forms/AdditionalInsureeInfoForm/AdditionalContactInfoForm'
import { AdditionalHolderInfoForm } from '../../vehicle/Step3/forms/AdditionalInsureeInfoForm/AdditionalHolderInfoForm'
import { AdditionalOwnerInfoForm } from '../../vehicle/Step3/forms/AdditionalInsureeInfoForm/AdditionalOwnerInfoForm'
import { AdditionalPolicyHolderInfoForm } from '../../vehicle/Step3/forms/AdditionalInsureeInfoForm/AdditionalPolicyHolderInfoForm'
import { VehicleAdditionalInfoForm } from '../../vehicle/Step3/forms/VehicleAdditionalInfoForm/VehicleAdditionalInfoForm'

export type Step3FormValues = WizardVehicleAdditionalData

export type Step3FormProps = {
  data?: Step3FormValues
}

export const Step3 = ({ data }: Step3FormProps) => {
  const { product, app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas

  const navigate = useNavigate()
  const { insuranceParams, refetchInsuranceParams, insuranceParamsId: paramsId, isDisabled } = useInsuranceParams()
  const { mutateAsync: saveAdditionalInfo } = useSaveAdditionalData()
  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { offer } = useSelectedQuote()

  const { isOpen: isOpenAgreementConfirmationModal, onOpen: onOpenAgreementConfirmationModal, onClose: onCloseAgreementConfirmationModal } = useDisclosure()

  const nameof = useNameof<Step3FormValues>()
  const personPrefix = nameof(x => x.policyHolder.person)

  const formDefaultValues: Step3FormValues = {
    ...data!,
    nextPage: 'next'
  }

  const methods = useForm<Step3FormValues>({
    values: formDefaultValues,
    resolver: yupResolver(yup.object().shape({})) as any, // aby se pripadne resetovaly serverove validace
    mode: 'onBlur',
  })

  // v nevalidniho formulare se nascroluje na validationSummary
  const validationSummaryRef = useRef<HTMLDivElement>(null)
  const scrollToValidationSummary = useCallback(() => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }, [validationSummaryRef])

  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    setError,
    getValues,
    reset,
    watch
  } = methods

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.VehicleInsurance, 3, paramsId, methods)

  const submitForm = handleSubmit((data) => {
    const values = cloneDeep(data)

    return saveAdditionalInfo(values)
      .then(async (response) => {
        reset(data)
        insuranceParamsFormPersistance.clear()

        if (values.nextPage == 'next') {
          if ((insuranceParams?.generalParams.lastWizardStep ?? 3) == 3) {
            await updateStep({ paramsId: Number(paramsId), step: 4 })
            await refetchInsuranceParams()
          }

          // aby se stihl resetovat formular a smazal local storage pred navigaci (jinak se zobrazuje okno s neulozenymi daty)
          setTimeout(() => {
            navigate(`/poleas/wizard/4`)
          }, 0)
        }
      })
      .catch((err) => {
        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })

        scrollToValidationSummary()
      })
      .finally(() => { })
  }, (err) => {
    scrollToValidationSummary()
  })

  const showVinkulant = getValues('policyHolder.showVinkulant')
  const { pageSections, setPageSections } = useSidebar()
  useEffect(() => {
    let i = 1
    const array: Section[] = []

    array.push(
      {
        id: 'section_' + Step3Sections.vozidlo,
        index: i,
        sectionTitle: 'Vozidlo',
        children: (
          <FormSection number={i++} sectionTitle="Vozidlo">
            <VehicleAdditionalInfoForm />
          </FormSection>
        )
      }
    )

    array.push(
      {
        id: 'section_' + Step3Sections.pojistnik,
        index: i,
        sectionTitle: 'Pojistník',
        children: (
          <FormSection number={i++} sectionTitle="Pojistník">
            <AdditionalPolicyHolderInfoForm personPrefix={personPrefix} simpleShow />
          </FormSection>
        )
      }
    )

    array.push(
      {
        id: 'section_' + Step3Sections.drzitel,
        index: i,
        sectionTitle: 'Držitel',
        children: (
          <FormSection number={i++} sectionTitle="Držitel/provozovatel vozidla">
            <AdditionalHolderInfoForm isDisabledChanges />
          </FormSection>
        )
      }
    )

    array.push(
      {
        id: 'section_' + Step3Sections.vlastnik,
        index: i,
        sectionTitle: 'Vlastník',
        children: (
          <FormSection number={i++} sectionTitle="Vlastník">
            <AdditionalOwnerInfoForm isDisabledChanges />
          </FormSection>
        )
      }
    )

    array.push(
      {
        id: 'section_' + Step3Sections.adresa,
        index: i,
        sectionTitle: 'Korespondenční adresa',
        children: (
          <FormSection number={i++} sectionTitle="Korespondenční adresa">
            <AdditionalContactInfoForm />
          </FormSection>
        )
      }
    )

    setPageSections(array)
  }, [product, showVinkulant])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Stack gap={8}>
          {pageSections.map(x => (
            <CustomWaypoint key={x.id} sectionId={x.id}>
              <PageSection id={x.id}>
                {x.children}
              </PageSection>
            </CustomWaypoint>
          ))}

          {!isDisabled &&
            < Flex justifyContent='center'>
              <Button
                type="button"
                variant="solid"
                colorScheme="green"
                isLoading={isSubmitting}
                onClick={() => {
                  if (isPublicPoleas && getValues('policyHolder.person.agreement') != true) {
                    onOpenAgreementConfirmationModal()
                  }
                  else
                    submitForm()
                }}
              >
                Pokračovat
              </Button>
            </Flex>
          }

          <ErrorSummary errors={errors} ref={validationSummaryRef} />
          <UnsavedFormModal {...insuranceParamsFormPersistance} />
          <ConfirmModal
            isOpen={isOpenAgreementConfirmationModal}
            onClose={onCloseAgreementConfirmationModal}
            title="Nepotvrzen souhlas s el. komunikací"
            isCentered
            noButtonContent="Ne"
            yesButton={(
              <Button
                colorScheme="green"
                onClick={() => {
                  onCloseAgreementConfirmationModal()
                  submitForm()
                }}
              >
                Ano
              </Button>
            )}
          >
            Bez souhlasu s elektronickou komunikací nebude možné nyní sjednat smlouvu. Chcete přesto pokračovat?
          </ConfirmModal>
        </Stack>
      </form >
    </FormProvider >
  )
}
